import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  type DragCancelEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ProjectListItem } from '../../services/api/projects';
import type { ExternalProjectNode } from '../../services/api/externalProjects';
import type {
  TaskCard as TaskCardData,
  BoardResponse,
  BoardSubproject,
} from '../../services/api/board';
import { useBoard } from '../../hooks/useBoard';
import { useTaskMutation } from '../../hooks/useTaskMutation';
import { useProject } from '../../hooks/useProjects';
import {
  useCreateProjectState,
  useDeleteProjectState,
  useReorderProjectStates,
  useUpdateProjectState,
} from '../../hooks/useProjectStates';
import { isStateInUseError } from '../../services/api/projectStates';
import { useAuthOptional } from '../../services/authContext';
import { KanbanSwimlane } from '../../components/kanban/KanbanSwimlane';
// MKS-292 — board-scoped state for the red "blocked by / blocks" hover highlight.
import { BlockerHighlightProvider } from '../../components/kanban/blockerHighlightContext';
import { InternalChipVisibilityProvider } from '../../components/kanban/internalChipVisibility';
import { AddStateColumn } from '../../components/kanban/AddStateColumn';
import { TaskCardDragOverlay } from '../../components/kanban/TaskCardDragOverlay';
import { MilestoneRowDragOverlay } from '../../components/kanban/MilestoneRowDragOverlay';
// Phase 41 Plan 41-17 (Task 2) — TaskDetailModal replaced by
// TaskDetailSlideOver per CONTEXT D-TaskSlideOver.
import { TaskDetailSlideOver } from '../../components/kanban/TaskDetailSlideOver';
import { MilestoneFormModal } from '../../components/kanban/MilestoneFormModal';
import { ConfirmModal } from '../../components/common/Modal';
import { useAssignTaskMilestone } from '../../hooks/useAssignTaskMilestone';
import { useDeleteMilestone, useMilestones } from '../../hooks/useMilestones';
import { useMilestoneOrder } from '../../hooks/useMilestoneOrder';
import type { Milestone } from '../../services/api/milestones';
import { ExternalCreateTaskButton } from '../../components/kanban/ExternalCreateTaskButton';
import { ExternalCreateTaskModal } from '../../components/kanban/ExternalCreateTaskModal';
import { InviteExternalModal } from '../../components/kanban/InviteExternalModal';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
// Phase 51 Plan 51-07 INTAKE-02 — distinct Triage column with inline triage actions.
import { TriageColumn } from '../../components/kanban/TriageColumn';
import { useProjectSlaTiers } from '../../hooks/useProjectSlaTiers';
// Quick task 260611-gre — Board/List switcher + Linear-style list view.
import { ProjectEngineeringLink } from '../../components/engineering/ProjectEngineeringLink';
import { BoardListSwitcher } from '../../components/kanban/BoardListSwitcher';
import { ProjectListView } from '../../components/kanban/ProjectListView';
// 260913-jpy — resolves the per-project staleness indicator config, with a
// disabled default while projectQuery.data is not yet loaded (or not
// enabled, e.g. the external audience).
import { projectStalenessConfig } from '../../utils/format';
import { buildSubprojectLanes } from '../../utils/subprojectLanes';
// MKS-142 — client-side full-text search over ticket code/name/description.
import { filterBoardColumnsBySearch, normalizeSearchText } from '../../utils/taskSearch';
import {
  sortBoardColumns,
  readInitialBoardSortMode,
  persistBoardSortMode,
  type BoardSortMode,
} from '../../utils/boardSort';
import { BoardSortControl } from '../../components/kanban/BoardSortControl';
// MKS-500 — the slim/full column-header toggle, moved out of the swimlane's
// corner cell into this toolbar.
import { SlimHeaderToggle } from '../../components/kanban/SlimHeaderToggle';
import { useSlimHeaderPreference } from '../../hooks/useSlimHeader';
import { useFlag } from '../../services/flagsContext';
// MKS-134 — kind-aware board filters (assignee / status / milestone / dates).
import { BoardFilterBar, type BoardStatusOption } from '../../components/kanban/BoardFilterBar';
import { PriorityFilterToggle } from '../../components/kanban/PriorityFilterToggle'; // MKS-163 follow-up
import {
  collectBoardAssignees,
  collectBoardCreators, // MKS-510
  collectBoardLabels, // MKS-164
  filterBoardColumns,
  statusFilterKey,
  type BoardFilterState,
} from '../../utils/boardFilters';
// MKS-168 — board filters + search are now URL-persisted (shareable views);
// see the module docblock for the shared key vocabulary with the server.
import {
  BOARD_FILTER_URL_PARAM_KEYS,
  parseBoardFiltersFromUrl,
  serializeBoardFiltersToParams,
} from '../../utils/boardFilterUrl';

// ---------------------------------------------------------------------------
// Quick task 260611-gre — per-project Board/List toggle, persisted to
// sessionStorage (transient — clears on browser reopen). Mirrors the
// MyTasksProjectGroup convention (frontend/src/components/mytasks/MyTasksProjectGroup.tsx
// lines 28-52). CONTEXT calls this "the existing usePreferences pattern" — the
// actual sessionStorage convention lives in MyTasksProjectGroup, NOT in
// hooks/usePreferences (that hook is server-state for user preferences).
// ---------------------------------------------------------------------------
type ProjectViewMode = 'board' | 'list';

function viewModeStorageKey(projectId: string): string {
  return `projectview.${projectId}`;
}

function readViewMode(projectId: string): ProjectViewMode {
  if (typeof window === 'undefined') return 'board';
  try {
    const v = window.sessionStorage.getItem(viewModeStorageKey(projectId));
    return v === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

function writeViewMode(projectId: string, mode: ProjectViewMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(viewModeStorageKey(projectId), mode);
  } catch {
    // sessionStorage unavailable (private browsing edge) — silent fallback.
  }
}

// ---------------------------------------------------------------------------
// MKS-419 — "Include subprojects" preference, per project, in sessionStorage.
// Same convention (and same silent fallback) as the Board/List toggle above.
// ---------------------------------------------------------------------------
function subprojectsStorageKey(projectId: string): string {
  return `projectboard.subprojects.${projectId}`;
}

function readSubprojectsPreference(projectId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(subprojectsStorageKey(projectId)) === 'true';
  } catch {
    return false;
  }
}

function writeSubprojectsPreference(projectId: string, on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(subprojectsStorageKey(projectId), on ? 'true' : 'false');
  } catch {
    // sessionStorage unavailable (private browsing edge) — silent fallback.
  }
}

export interface KanbanBoardPageProps {
  audience: 'internal' | 'external';
}

/**
 * Phase 24 D-13 — top-level page replacing KanbanPlaceholderPage at both
 * internal /projects/:id/board and external /external/projects/:id/board
 * routes. Same component for both surfaces; audience prop drives the only
 * branching (no conditional auth-hook imports — audience is the seam).
 *
 * useParams<{ id: string }>() preserves Phase 23 B3 contract.
 *
 * MKS-292 — the board is the scope of the blocker hover highlight: hovering one
 * card's "Blocked by" line has to ring cards in OTHER columns, so the hovered
 * counterpart ids live in a provider mounted here, above the whole board, and
 * every TaskCard reads them. A thin wrapper keeps that provider out of the page
 * body, which must stay one component for its own hook order.
 *
 * MKS-534 — the "does this project have anyone external on it" answer is a
 * board-level fact too (one query for every card), so its provider sits in the
 * same wrapper, above the cards, the list rows and the drag ghost alike.
 */
export function KanbanBoardPage(props: KanbanBoardPageProps) {
  // Same param the page body reads; `Number('abc')` is NaN, which the provider
  // takes as "no project" and therefore hides nothing.
  const { id: idParam } = useParams<{ id: string }>();
  const projectIdNum = Number(idParam);

  return (
    <BlockerHighlightProvider>
      <InternalChipVisibilityProvider
        projectId={Number.isFinite(projectIdNum) ? projectIdNum : null}
        audience={props.audience}
      >
        <KanbanBoardPageContent {...props} />
      </InternalChipVisibilityProvider>
    </BlockerHighlightProvider>
  );
}

function KanbanBoardPageContent({ audience }: KanbanBoardPageProps) {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // BLOCKER 1 — store the id, derive the live row from cache via useMemo.
  // After any mutation that patches the cache (useUpdateTask in particular),
  // the next render re-resolves selectedTask to the freshest row → modal
  // heading + description reflect the post-mutation values automatically.
  // If the row disappears (e.g. successful delete), this returns null and
  // the modal closes naturally because isOpen becomes false.
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  // Phase 26 D-12 — `?openTask={id}` lookup miss renders an inline notice.
  const [openTaskNotFound, setOpenTaskNotFound] = useState(false);
  // MKS-384 — the optional `?comment={id}` that rides along with `?openTask`:
  // a Slack DM about ONE Factory™ question links to that question's own
  // comment, so the thread opens there instead of at the bottom. Read and
  // cleared by the same effect that handles `?openTask`.
  const [deepLinkCommentId, setDeepLinkCommentId] = useState<number | null>(null);
  const [toast, setToast] = useState<AppToastState | null>(null);
  // Phase 25 Plan 25-07 — external `+ New task` modal state. Only ever read
  // when audience === 'external' (the launcher is gated by audience).
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const boardScrollRef = useRef<HTMLDivElement | null>(null);
  // MKS-134 — board filter state (assignee / status / milestone / created /
  // last update). The assignee slot uses kind-aware composite string keys
  // ('u:19' / 'e:5' / 'unassigned') — the old numeric assigneeId sentinel
  // could not represent external assignees, which was the MKS-134 bug.
  // MKS-168 — REVERSES the earlier D-03 "ephemeral, never written to the
  // URL" decision: filters now round-trip through the URL (shareable/
  // bookmarkable filtered views), hydrated here via a lazy initializer so
  // the very first render already reflects a copy-pasted link. A synced
  // effect below writes changes back with `setSearchParams` (merged,
  // replace-navigation — see that effect's comment).
  const [filters, setFilters] = useState<BoardFilterState>(
    () => parseBoardFiltersFromUrl(searchParams).filters,
  );

  // MKS-510 — the Creator slot ships behind a release flag. The flag is read
  // here, not inside the lazy initializer above: flags arrive asynchronously
  // (FlagsProvider is fail-closed while its query loads), so a shared
  // `?creator=` link must still be parsed into state on first render and only
  // its EFFECT gated — otherwise the link would lose the filter in the
  // milliseconds before the flag resolves. With the flag off the slot is
  // nulled out of everything downstream (the predicates below and the URL
  // sync), and BoardFilterBar is given no `creators` prop, so the board is
  // exactly today's board.
  const boardCreatorFilter = useFlag('mks_510_board_creator_filter');
  const creatorGatedFilters = useMemo<BoardFilterState>(
    () =>
      filters.creator !== null && !boardCreatorFilter ? { ...filters, creator: null } : filters,
    [filters, boardCreatorFilter],
  );

  // Quick 260916-fk0 — column ordering. Seeded from sessionStorage and
  // validated there, so a tampered value can never reach the comparator.
  const [sortMode, setSortModeState] = useState<BoardSortMode>(readInitialBoardSortMode);
  const setSortMode = useCallback((next: BoardSortMode) => {
    setSortModeState(next);
    persistBoardSortMode(next);
  }, []);
  // MKS-142 — full-text search over ticket code/name/description. MKS-168 —
  // same reversal as `filters` above: now URL-persisted (`?q=`) for the same
  // shareable-view reason, hydrated from the URL on first render.
  const [searchQuery, setSearchQuery] = useState<string>(
    () => parseBoardFiltersFromUrl(searchParams).searchQuery,
  );

  // MKS-168 — writes `filters` + `searchQuery` back to the URL whenever
  // either changes. Two hard requirements: (1) merge into the EXISTING
  // params (mutating the `prev` URLSearchParams in place, mirroring the
  // `openTask` deep-link handler below) so the page's own `openTask` param
  // is never clobbered; (2) `{ replace: true }` so dragging a date filter or
  // typing a search character doesn't push a history entry per keystroke.
  // Every filter-owned key is cleared first so a slot that just became
  // inactive (or a date axis whose mode just changed) doesn't leave a stale
  // value behind.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        for (const key of BOARD_FILTER_URL_PARAM_KEYS) prev.delete(key);
        // MKS-510 — serialized from the creator-GATED state, so a flag-off
        // board never writes `?creator=` back (not even a stale one it was
        // opened with).
        const serialized = serializeBoardFiltersToParams(creatorGatedFilters, searchQuery);
        for (const [key, value] of Object.entries(serialized)) prev.set(key, value);
        return prev;
      },
      { replace: true },
    );
  }, [creatorGatedFilters, searchQuery, setSearchParams]);

  const projectId = idParam ?? '';
  const projectIdNum = Number(idParam);

  // Quick task 260611-gre — Board/List toggle persisted per project in
  // sessionStorage (see module-scope helpers above; mirrors MyTasksProjectGroup).
  // Lazy initializer uses projectId (declared above) so sessionStorage read
  // happens at first render, not on every re-render.
  const [viewMode, setViewMode] = useState<ProjectViewMode>(() => readViewMode(projectId));
  const handleViewModeChange = useCallback(
    (next: ProjectViewMode) => {
      setViewMode(next);
      writeViewMode(projectId, next);
    },
    [projectId],
  );

  // MKS-500 — the slim/full column-header choice. Owned here (not inside
  // KanbanSwimlane as MKS-378 had it) so the control can live in the toolbar
  // below; the swimlane gets the value, which is also what tells it to stop
  // rendering its own corner-cell button.
  const { slimHeader, toggleSlimHeader } = useSlimHeaderPreference();

  // MKS-419 — the merged parent + subprojects board. Needs the project (for
  // `_count.children`), so the project query is declared ABOVE useBoard now.
  const projectQuery = useProject(audience === 'internal' ? projectIdNum : undefined);
  const hasSubprojects = (projectQuery.data?._count?.children ?? 0) > 0;
  const showSubprojectsToggle =
    audience === 'internal' && viewMode === 'board' && hasSubprojects;
  const [subprojectsPreference, setSubprojectsPreference] = useState<boolean>(() =>
    readSubprojectsPreference(projectId),
  );
  const includeSubprojects = showSubprojectsToggle && subprojectsPreference;
  const { data, isLoading, isError, refetch } = useBoard(
    projectId,
    audience,
    includeSubprojects ? { includeSubprojects: true } : undefined,
  );
  // Spread into task mutations so the optimistic update targets the merged cache
  // entry; absent (not `false`) while the view is off, so today's calls are unchanged.
  const mergedMutationOpts = includeSubprojects ? { includeSubprojects: true as const } : {};
  const subprojectsById = useMemo<ReadonlyMap<number, BoardSubproject> | undefined>(
    () =>
      includeSubprojects
        ? new Map((data?.subprojects ?? []).map((sp) => [sp.id, sp] as const))
        : undefined,
    [includeSubprojects, data?.subprojects],
  );

  // Phase 51 Plan 51-07 INTAKE-02 — fetch SLA tiers for the TriageColumn tier picker.
  // Only fetched for internal audience on SLA projects; enabled check is inside the hook.
  const slaTiersQuery = useProjectSlaTiers(
    audience === 'internal' ? projectIdNum : undefined,
  );
  // useProjectSlaTiers wraps the API response — actual array is at .data.data
  const slaTiers = slaTiersQuery.data?.data ?? [];

  // Column reorder gate: internal audience + (admin OR project PM). Mirrors the
  // canEditStates contract on ProjectFormPage so the kanban exposes the same
  // ordering authority as the project edit page (no new permission surface).
  // Uses the optional auth hook so test scaffolds and the external surface
  // can render this page without an AuthProvider — gate just fails closed.
  const auth = useAuthOptional();
  const currentUser = auth?.user ?? null;
  const isAdmin = auth?.isAdmin ?? false;
  // 260913-jpy — the resolver returns a disabled config for a nullish
  // project, so the emoji stays suppressed while the query is loading and
  // for the external audience (where useProject is not even enabled) — no
  // flash of emoji before the config arrives.
  const stalenessConfig = useMemo(
    () => projectStalenessConfig(projectQuery.data),
    [projectQuery.data],
  );
  const canReorderColumns =
    audience === 'internal' &&
    !!currentUser &&
    (isAdmin || projectQuery.data?.projectManager?.id === currentUser.id);
  // Phase 30 D-10 — single source of truth for milestone-management gating;
  // same shape as canReorderColumns. Threaded to KanbanSwimlane AND
  // TaskDetailSlideOver (formerly TaskDetailModal — replaced in Phase 41
  // Plan 41-17) AND used to gate the swimlane-cell branch in onDragEnd.
  const canManageMilestones =
    audience === 'internal' &&
    !!currentUser &&
    (isAdmin || projectQuery.data?.projectManager?.id === currentUser.id);
  // Same authority as column reorder — admin OR project PM may invite externals
  // to this project from the kanban toolbar.
  const canInviteExternal = canReorderColumns;
  // Quick 260914-huu — a transport guard, NOT an authority gate like
  // canReorderColumns/canManageMilestones above. Milestone row order is a
  // private per-user view preference every internal viewer may set; this
  // only avoids a guaranteed-fail PATCH for external viewers, whose
  // /auth/preferences PATCH 401s by design (see client.ts:27-46).
  const canReorderMilestoneRows = audience === 'internal';
  // Quick task 260727-e7q — gate for linking the header project name to
  // /projects/:id. Mirrors the AdminOrProjectPMRoute gate on that route
  // (App.tsx:238) so the link only ever appears when the destination is
  // actually reachable for this viewer.
  const canOpenProjectDetail =
    audience === 'internal' &&
    !!currentUser &&
    (isAdmin || projectQuery.data?.projectManager?.id === currentUser.id);
  // Quick task 260610-kv6 — add / rename / recolor / delete states from the
  // board. Same gate shape as canReorderColumns; reusing the alias keeps the
  // contract aligned with ProjectFormPage (every action gated on admin OR PM).
  const canManageStates = canReorderColumns;
  // MKS-419 — a merged column spans several projects' states, so column
  // management is off while the merged view is on: the parent's state API must
  // never be pointed at a child's state id.
  const canReorderColumnsHere = canReorderColumns && !includeSubprojects;
  const canManageStatesHere = canManageStates && !includeSubprojects;
  const reorderColumnsMutation = useReorderProjectStates(projectIdNum);
  const createStateMutation = useCreateProjectState(projectIdNum);
  const updateStateMutation = useUpdateProjectState(projectIdNum);
  const deleteStateMutation = useDeleteProjectState(projectIdNum);
  // Mirrors ProjectFormPage.tsx:131 — STATE_IN_USE banner state (D-20).
  const [deleteConflict, setDeleteConflict] = useState<{
    stateId: number;
    stateName: string;
    taskCount: number;
  } | null>(null);

  // Phase 30 — milestone CRUD modal state machine.
  type MilestoneModalState =
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; milestone: Milestone }
    | { kind: 'delete-confirm'; milestone: Milestone };
  const [milestoneModal, setMilestoneModal] = useState<MilestoneModalState>({
    kind: 'closed',
  });

  // Phase 30 D-05 — page-owned DndContext sensors (Phase 24 Pitfall 7 lock:
  // distance:8 keeps a click on a card opening the modal instead of dragging).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Phase 30 — task-milestone reassignment mutation (drag-onto-sidebar branch)
  // and milestone delete mutation (sidebar trash icon → ConfirmModal).
  const assignMilestoneMutation = useAssignTaskMilestone();
  const deleteMilestoneMutation = useDeleteMilestone(projectId);

  // Phase 30 D-05 — page now owns the activeTaskId for the DragOverlay so the
  // overlay travels with the cursor across the milestone sidebar AND board.
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);

  // Quick 260914-ims — page-owned active milestone-row id, mirroring
  // activeTaskId exactly (same reason: the ghost must reflect the freshest
  // polled milestone data, not a snapshot taken at drag start).
  const [activeMilestoneRowId, setActiveMilestoneRowId] = useState<number | null>(null);

  // Per-mutate onError below carries the type-specific wording (move vs visibility).
  // Do NOT pass a global onError — TanStack Query v5 fires both global + per-mutate
  // onError callbacks on failure, which would produce duplicate toasts.
  const mutation = useTaskMutation();

  // MKS-134 — kind-aware assignee options across ALL columns (Triage
  // included, so external assignees on triage cards are filterable too).
  const boardAssignees = useMemo(() => collectBoardAssignees(data?.columns ?? []), [data]);

  // MKS-164 — one filter option per label present on the loaded board.
  const boardLabels = useMemo(() => collectBoardLabels(data?.columns ?? []), [data]);

  // MKS-510 — kind-aware creator options across ALL columns, mirroring
  // boardAssignees above. Collected unconditionally (a cheap walk of the same
  // already-loaded columns); the flag decides whether they are HANDED to
  // BoardFilterBar, which is what makes the control exist at all.
  const boardCreators = useMemo(() => collectBoardCreators(data?.columns ?? []), [data]);

  // MKS-134 (external surface) — Triage is never rendered for the external
  // audience, in either view: board (`triageColumn && audience === 'internal'`
  // just below at the KanbanSwimlane branch) or list
  // (`ProjectListView.tsx:590-601` — `ExternalListSections` is never passed
  // `triageColumn`). `triageVisible` mirrors that render-time gate so the two
  // triage-dependent derivations below (statusOptions, filterCounts) stay in
  // lockstep with what the page actually shows, instead of leaking a status
  // option / count for a column the external user never sees on screen.
  const triageVisible = audience === 'internal';

  // MKS-134 — status options follow the board's own column order. MKS-134
  // (external surface): when Triage isn't rendered for this audience, its
  // status option is omitted too — offering "Triage" would otherwise empty
  // every visible column (filterBoardColumns empties non-matching columns
  // rather than dropping them) for a dead-end selection.
  const statusOptions = useMemo<BoardStatusOption[]>(
    () =>
      data == null
        ? []
        : data.columns
            .filter((col) => triageVisible || statusFilterKey(col) !== 'triage')
            .map((col) => ({
              key: statusFilterKey(col),
              name: col.name,
              color: col.color ?? null,
            })),
    [data, triageVisible],
  );

  // MKS-134 — milestone filter options. The query key ['milestones', projectId]
  // is already populated by KanbanSwimlane, so this is a cache hit, not a
  // second network request.
  const milestonesQuery = useMilestones(projectId, audience);
  const milestoneOptions = useMemo(
    () => (milestonesQuery.data ?? []).map((m) => ({ id: m.id, name: m.name })),
    [milestonesQuery.data],
  );

  // MKS-184 — a board with no milestones renders a single swimlane row, so the
  // board can be sized to the scroll viewport and each column given its own
  // vertical scroll; the page then never scrolls vertically at all. While the
  // milestone list is loading or failed this stays false, i.e. the board keeps
  // today's page-scroll layout until we know which shape it is.
  const boardFillsViewport =
    milestonesQuery.isSuccess && milestonesQuery.data.length === 0;

  // Quick 260914-huu — this page holds a second useMilestoneOrder instance
  // alongside KanbanSwimlane's own. Both read the same ['preferences'] cache
  // through the same pure orderMilestones function, so they cannot diverge;
  // only THIS instance ever calls onReorder (the swimlane's copy is
  // read-only — it just applies the overlay to what it renders).
  const { orderMilestones, onReorder: onReorderMilestones } = useMilestoneOrder(projectId);
  const orderedMilestoneIds = useMemo(
    () => orderMilestones(milestonesQuery.data ?? []).map((m) => m.id),
    [orderMilestones, milestonesQuery.data],
  );

  // Phase 51 Plan 51-07 INTAKE-02 — separate the synthetic Triage column from
  // the regular board columns. TriageColumn renders independently to the left of
  // KanbanSwimlane; KanbanSwimlane receives only the non-triage columns.
  const triageColumn = useMemo(
    () => (data ? data.columns.find((c) => c.isTriage) ?? null : null),
    [data],
  );
  const nonTriageData = useMemo(() => {
    if (!data) return data;
    const cols = data.columns.filter((c) => !c.isTriage);
    if (cols.length === data.columns.length) return data; // no triage column → no-op
    return { ...data, columns: cols };
  }, [data]);

  // MKS-419 — the Project filter slot only applies while the merged view is on.
  // A stale `?project=` (shared link, view switched off) must narrow nothing.
  // MKS-510 — layered on top of the creator gate, so a flag-off board filters
  // with the Creator slot inert as well.
  const effectiveFilters = useMemo<BoardFilterState>(
    () =>
      creatorGatedFilters.subproject !== null && !includeSubprojects
        ? { ...creatorGatedFilters, subproject: null }
        : creatorGatedFilters,
    [creatorGatedFilters, includeSubprojects],
  );

  // MKS-134 — filter-bar stage (assignee / status / milestone / dates).
  // Column structure stays intact so empty cells still render (drag targets
  // must exist even when filtered, D-04). Identity short-circuit per D-05
  // keeps downstream props referentially stable while no filter is active.
  const barFilteredData = useMemo(() => {
    if (!nonTriageData) return nonTriageData;
    const columns = filterBoardColumns(nonTriageData.columns, effectiveFilters);
    return columns === nonTriageData.columns ? nonTriageData : { ...nonTriageData, columns };
  }, [nonTriageData, effectiveFilters]);

  // MKS-134 D-06 — unlike the pre-MKS-134 assignee filter (which skipped
  // Triage), the filter bar DOES reach the Triage column.
  const barFilteredTriageColumn = useMemo(() => {
    if (triageColumn == null) return null;
    return filterBoardColumns([triageColumn], effectiveFilters)[0] ?? null;
  }, [triageColumn, effectiveFilters]);

  // MKS-142 — layer the text search on top of the filter bar (the two stack;
  // neither replaces the other). The identity check keeps KanbanSwimlane /
  // ProjectListView props referentially stable while the search field is empty.
  const filteredData = useMemo(() => {
    if (!barFilteredData) return barFilteredData;
    const columns = filterBoardColumnsBySearch(barFilteredData.columns, searchQuery);
    if (columns === barFilteredData.columns) return barFilteredData;
    return { ...barFilteredData, columns };
  }, [barFilteredData, searchQuery]);

  // MKS-134 D-06 — the filter bar applies to Triage (via barFilteredTriageColumn)
  // and the MKS-142 search runs second, so both stages reach the Triage column.
  const filteredTriageColumn = useMemo(() => {
    if (!barFilteredTriageColumn) return null;
    return filterBoardColumnsBySearch([barFilteredTriageColumn], searchQuery)[0] ?? null;
  }, [barFilteredTriageColumn, searchQuery]);

  // Quick 260916-fk0 — ordering runs LAST, on top of the filter bar and the
  // search, and reaches the Triage column via the parallel chain below. In
  // 'default' mode sortBoardColumns returns its input by identity, so this
  // stage is a no-op for props stability exactly like the filter stages above.
  const sortedData = useMemo(() => {
    if (!filteredData) return filteredData;
    const columns = sortBoardColumns(filteredData.columns, sortMode);
    return columns === filteredData.columns ? filteredData : { ...filteredData, columns };
  }, [filteredData, sortMode]);

  const sortedTriageColumn = useMemo(() => {
    if (!filteredTriageColumn) return null;
    return sortBoardColumns([filteredTriageColumn], sortMode)[0] ?? null;
  }, [filteredTriageColumn, sortMode]);

  // MKS-419 — one swimlane row per subproject that has cards on the board AFTER the
  // filters and the search (empty subproject lanes are hidden). `undefined` while the
  // merged view is off, which keeps the swimlane exactly today's.
  const subprojectLanes = useMemo(
    () =>
      includeSubprojects && sortedData
        ? buildSubprojectLanes(data?.subprojects ?? [], projectIdNum, sortedData.columns)
        : undefined,
    [includeSubprojects, sortedData, data?.subprojects, projectIdNum],
  );
  // The Project filter narrowed to ONE subproject: only its lane shows. Narrowed to
  // the parent (or not narrowed) the parent's rows stay.
  const hideParentRows =
    includeSubprojects &&
    effectiveFilters.subproject !== null &&
    effectiveFilters.subproject !== `p:${projectIdNum}`;
  // MKS-184's single-row fill layout needs a board that is exactly one row. A
  // subproject lane makes it multi-row, so the fill must not apply then.
  const boardFillsViewportHere =
    boardFillsViewport && (subprojectLanes === undefined || subprojectLanes.length === 0);

  const searchActive = normalizeSearchText(searchQuery).length > 0;

  // MKS-142 — "shown / total" badge counts. Total is post-filter-bar so the
  // badge reflects what the search itself is narrowing.
  const searchCounts = useMemo(() => {
    const total =
      (barFilteredData?.columns.reduce((acc, col) => acc + col.tasks.length, 0) ?? 0) +
      (barFilteredTriageColumn?.tasks.length ?? 0);
    const shown =
      (filteredData?.columns.reduce((acc, col) => acc + col.tasks.length, 0) ?? 0) +
      (filteredTriageColumn?.tasks.length ?? 0);
    return { shown, total };
  }, [barFilteredData, filteredData, barFilteredTriageColumn, filteredTriageColumn]);

  // MKS-134 — "shown / total" for the filter bar itself. Total is the RAW
  // board task count, Triage included ONLY when the audience actually renders
  // it (triageVisible — internal). MKS-134 (external surface): for external
  // users both sides of the ratio skip the triage column/task so the badge
  // reflects what the external surface actually shows on screen.
  const filterCounts = useMemo(() => {
    const total =
      (nonTriageData?.columns.reduce((acc, col) => acc + col.tasks.length, 0) ?? 0) +
      (triageVisible ? triageColumn?.tasks.length ?? 0 : 0);
    const shown =
      (barFilteredData?.columns.reduce((acc, col) => acc + col.tasks.length, 0) ?? 0) +
      (triageVisible ? barFilteredTriageColumn?.tasks.length ?? 0 : 0);
    return { shown, total };
  }, [nonTriageData, triageColumn, triageVisible, barFilteredData, barFilteredTriageColumn]);

  // Resolve project name from existing TanStack Query caches (mirrors
  // KanbanPlaceholderPage). On a hard refresh the sidebar caches may not be
  // populated yet — fall back to the project-detail query (already fetched
  // for the column-reorder gate) before degrading to the literal id.
  const projectName = useMemo(() => {
    if (!Number.isFinite(projectIdNum)) return idParam ?? 'Project';
    if (audience === 'internal') {
      const cache = queryClient.getQueryData<ProjectListItem[]>(['my-projects']);
      const hit = cache?.find((p) => p.id === projectIdNum);
      return hit?.name ?? projectQuery.data?.name ?? `Project ${idParam}`;
    }
    const cache = queryClient.getQueryData<ExternalProjectNode[]>(['my-external-projects']);
    const hit = cache?.find((p) => p.id === projectIdNum);
    return hit?.name ?? `Project ${idParam}`;
  }, [audience, idParam, projectIdNum, queryClient, projectQuery.data?.name]);

  // MKS-419 — Project filter options (merged view only): the parent itself,
  // then each readable subproject. Keys are `p:<id>`, the vocabulary the URL
  // serializer and filterBoardColumns share.
  const projectFilterOptions = useMemo(
    () =>
      includeSubprojects
        ? [
            {
              key: `p:${projectIdNum}`,
              label: `${projectQuery.data?.name ?? `Project ${projectIdNum}`} (this project)`,
            },
            ...(data?.subprojects ?? []).map((sp) => ({
              key: `p:${sp.id}`,
              label: sp.name ? `${sp.slug} \u00b7 ${sp.name}` : sp.slug,
            })),
          ]
        : undefined,
    [includeSubprojects, projectIdNum, projectQuery.data?.name, data?.subprojects],
  );

  const handleSubprojectsToggle = useCallback(
    (next: boolean) => {
      setSubprojectsPreference(next);
      writeSubprojectsPreference(projectId, next);
      // Turning the view off clears the Project slot; it means nothing without it.
      if (!next) {
        setFilters((f) => (f.subproject === null ? f : { ...f, subproject: null }));
      }
    },
    [projectId],
  );

  // BLOCKER 1 — derive selectedTask from cached data.columns on every render.
  // After any mutation that patches the cache (useUpdateTask in particular),
  // the next render re-resolves selectedTask to the freshest row → modal
  // heading + description reflect the post-mutation values automatically.
  // If the row disappears (e.g. successful delete), this returns null and
  // the modal closes naturally because isOpen becomes false.
  // Phase 51 INTAKE-02: also searches the triage column tasks.
  const selectedTask = useMemo<TaskCardData | null>(() => {
    if (selectedTaskId === null || !data) return null;
    for (const column of data.columns) {
      const found = column.tasks.find((t) => t.id === selectedTaskId);
      if (found) return found;
    }
    return null;
  }, [selectedTaskId, data]);

  // MKS-419 — a SUBPROJECT card opens the normal task detail with that
  // subproject's own context (columns, task type, staleness config, PM
  // authority), mirroring TaskDetailPage which feeds the slide-over from the
  // task's OWN project. The own-project board query keeps the plain 3-element
  // key and is idle (empty id) for a parent card.
  const selectedSubprojectId =
    includeSubprojects && selectedTask && selectedTask.projectId !== projectIdNum
      ? selectedTask.projectId
      : undefined;
  const ownBoardQuery = useBoard(
    selectedSubprojectId ? String(selectedSubprojectId) : '',
    'internal',
  );
  const ownProjectQuery = useProject(selectedSubprojectId);
  const ownStalenessConfig = useMemo(
    () => projectStalenessConfig(ownProjectQuery.data),
    [ownProjectQuery.data],
  );
  const canManageOwnProject =
    !!currentUser &&
    (isAdmin || ownProjectQuery.data?.projectManager?.id === currentUser.id);
  const slideOverTask = useMemo<TaskCardData | null>(() => {
    if (selectedSubprojectId === undefined || !selectedTask) return selectedTask;
    // Prefer the own-project card: it stays fresh after the slide-over's own
    // cache patches (which target the subproject's board key).
    for (const column of ownBoardQuery.data?.columns ?? []) {
      const found = column.tasks.find((t) => t.id === selectedTask.id);
      if (found) return found;
    }
    return selectedTask;
  }, [selectedSubprojectId, selectedTask, ownBoardQuery.data]);

  // Phase 30 D-05 — re-derive the active task on every render so the
  // DragOverlay always reflects the freshest polled card (T-30-65 mitigation).
  const activeTask = useMemo<TaskCardData | undefined>(() => {
    if (activeTaskId == null || !data) return undefined;
    for (const col of data.columns) {
      const found = col.tasks.find((t) => t.id === activeTaskId);
      if (found) return found;
    }
    return undefined;
  }, [activeTaskId, data]);
  const activeTaskSourceStateId = activeTask?.taskStateId ?? null;

  // Quick 260914-ims — re-derive the active milestone row on every render,
  // same reasoning as activeTask above. milestonesQuery.data is the same
  // list the row header already renders (T-260914-ims-01 mitigation).
  const activeMilestoneRow = useMemo<Milestone | undefined>(() => {
    if (activeMilestoneRowId == null) return undefined;
    return milestonesQuery.data?.find((m) => m.id === activeMilestoneRowId);
  }, [activeMilestoneRowId, milestonesQuery.data]);

  // Phase 26 D-12 — `?openTask={taskId}` deep-link handler.
  // After the board fetch completes, look up the cached task by id; open the
  // detail modal if present, otherwise render an inline 'no longer available'
  // notice. Clear the param via setSearchParams replace so the back button
  // doesn't pogo and a subsequent close doesn't re-open the modal. Internal
  // and external surfaces share this code path (D-13) — the external board
  // cache already filters non-is_external tasks so externals naturally cannot
  // open internal tasks via this path.
  useEffect(() => {
    const openTaskParam = searchParams.get('openTask');
    if (openTaskParam === null) return;
    if (!data) return; // wait for the board fetch to complete

    const targetId = Number(openTaskParam);
    const isValidId =
      Number.isFinite(targetId) && Number.isInteger(targetId) && targetId > 0;

    if (!isValidId) {
      setSearchParams(
        (prev) => {
          prev.delete('openTask');
          prev.delete('comment'); // MKS-384 — it only ever rides with openTask
          return prev;
        },
        { replace: true },
      );
      return;
    }

    // MKS-384 — same strict shape as `openTask`: a positive integer or nothing.
    // Anything else is simply no deep link; it never blocks opening the task.
    const commentParam = Number(searchParams.get('comment'));
    const targetCommentId =
      Number.isInteger(commentParam) && commentParam > 0 ? commentParam : null;

    let found: TaskCardData | null = null;
    for (const column of data.columns) {
      const hit = column.tasks.find((t) => t.id === targetId);
      if (hit) {
        found = hit;
        break;
      }
    }

    if (found) {
      setSelectedTaskId(targetId);
      setOpenTaskNotFound(false);
      setDeepLinkCommentId(targetCommentId); // MKS-384
    } else {
      setOpenTaskNotFound(true);
    }

    setSearchParams(
      (prev) => {
        prev.delete('openTask');
        prev.delete('comment'); // MKS-384
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, data, setSearchParams]);

  function handleCardClick(task: TaskCardData) {
    setSelectedTaskId(task.id);
    setDeepLinkCommentId(null); // MKS-384 — a click is not a deep link
  }

  function handleCloseModal() {
    setSelectedTaskId(null);
    setDeepLinkCommentId(null); // MKS-384
  }

  function handleReorderColumns(orderedStateIds: number[]) {
    // Optimistic board cache reorder so the visual swap is instant; rollback
    // on error and refetch on settled to reconcile against the server.
    const boardKey = ['board', projectId, audience];
    const previous = queryClient.getQueryData<BoardResponse>(boardKey);
    if (previous) {
      const byStateId = new Map(previous.columns.map((c) => [c.taskStateId, c]));
      const next: BoardResponse = {
        columns: orderedStateIds
          .map((id) => byStateId.get(id))
          .filter((c): c is BoardResponse['columns'][number] => Boolean(c)),
      };
      queryClient.setQueryData(boardKey, next);
    }
    reorderColumnsMutation.mutate(orderedStateIds, {
      onError: (err) => {
        if (previous) queryClient.setQueryData(boardKey, previous);
        setToast({
          variant: 'error',
          message: `Couldn\u2019t reorder columns: ${err.message ?? 'Server error'}`,
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: boardKey });
      },
    });
  }

  // Quick task 260610-kv6 — state-management handlers (mirror
  // ProjectFormPage.tsx:478-557). Each surface errors via the existing toast;
  // delete surfaces the STATE_IN_USE conflict via the inline red banner so
  // the user gets a deep-link to the affected tasks (D-20 contract).
  //
  // Cross-cache invalidation: useCreateProjectState / useUpdateProjectState /
  // useDeleteProjectState only invalidate ['projectStates', projectId], which
  // ProjectFormPage / TaskManagementPage consume. The board uses a separate
  // cache key (['board', projectId, audience] — see hooks/useBoard.ts:17), so
  // without the explicit board invalidation below the new state would land
  // in the projectStates cache but the board columns would not re-fetch and
  // the new column would never appear (user UAT 2026-06-10 hit this on add).
  function invalidateBoard() {
    queryClient.invalidateQueries({ queryKey: ['board', projectId, audience] });
  }

  async function handleAddState(name: string, color: string) {
    try {
      // User UAT 2026-06-10 — backend Zod requires `ordering` on POST. The
      // canonical "append to end" semantic = count of existing non-triage
      // columns. data?.columns is the unfiltered board response (triage +
      // real); filter triage so adding a state on a triage-enabled project
      // doesn't double-count.
      const orderingBase =
        data?.columns.filter((c) => c.taskStateId !== null).length ?? 0;
      await createStateMutation.mutateAsync({
        name,
        color,
        ordering: orderingBase,
      });
      invalidateBoard();
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to add state',
      });
    }
  }

  async function handleRenameState(stateId: number, newName: string) {
    try {
      await updateStateMutation.mutateAsync({ stateId, patch: { name: newName } });
      invalidateBoard();
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to rename state',
      });
    }
  }

  async function handleRecolorState(stateId: number, newColor: string) {
    try {
      await updateStateMutation.mutateAsync({ stateId, patch: { color: newColor } });
      invalidateBoard();
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to recolor state',
      });
    }
  }

  // 260723-tst — flip a column's terminal flag from the board header.
  // 260726-wip — turning terminal ON clears the mutually-exclusive WIP flag
  // (the backend enforces this too; sending both keeps the optimistic UI correct).
  async function handleToggleTerminalState(stateId: number, next: boolean) {
    try {
      await updateStateMutation.mutateAsync({
        stateId,
        patch: next ? { isTerminal: true, isInProgress: false } : { isTerminal: false },
      });
      invalidateBoard();
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to update state',
      });
    }
  }

  // 260726-wip — flip a column's "work in progress" flag from the board header.
  // Turning WIP ON clears the mutually-exclusive terminal flag.
  async function handleToggleInProgressState(stateId: number, next: boolean) {
    try {
      await updateStateMutation.mutateAsync({
        stateId,
        patch: next ? { isInProgress: true, isTerminal: false } : { isInProgress: false },
      });
      invalidateBoard();
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to update state',
      });
    }
  }

  async function handleDeleteState(stateId: number) {
    setDeleteConflict(null);
    try {
      await deleteStateMutation.mutateAsync(stateId);
      invalidateBoard();
    } catch (err) {
      if (isStateInUseError(err)) {
        // STATE-04 / D-20 single surface — the inline red banner with the
        // [View tasks] deep-link is the authoritative UI. ProjectFormPage
        // sets the same single-banner-no-toast contract (no duplicate UX).
        const c = err.details.conflicts[0];
        if (c) {
          setDeleteConflict({
            stateId: c.stateId,
            stateName: c.stateName,
            taskCount: c.taskCount,
          });
        }
      } else {
        setToast({
          variant: 'error',
          message: err instanceof Error ? err.message : 'Failed to delete state',
        });
      }
    }
  }

  function handleFlip(task: TaskCardData) {
    mutation.mutate(
      {
        type: 'visibility',
        taskId: task.id,
        projectId,
        audience,
        toIsExternal: !task.isExternal,
        ...mergedMutationOpts,
      },
      {
        onError: (err) => {
          setToast({
            variant: 'error',
            message: `Couldn\u2019t update visibility: ${err.message ?? 'Server error'}`,
          });
        },
      },
    );
  }

  // Phase 30 D-05/D-06 — page-owned drag handlers covering all three branches.
  // Quick 260914-ims — split the 'milestone-row' branch off from 'column' so
  // a row drag sets activeMilestoneRowId (and renders its own ghost) instead
  // of silently rendering no overlay at all.
  function handleDragStart(event: DragStartEvent) {
    const dragType = event.active.data.current?.type;
    if (dragType === 'column') {
      // Column drags render no overlay at all.
      setActiveTaskId(null);
      setActiveMilestoneRowId(null);
      return;
    }
    if (dragType === 'milestone-row') {
      setActiveTaskId(null);
      const milestoneId = event.active.data.current?.milestoneId;
      // The synthetic "No milestone" row carries milestoneId: null and must
      // never produce a ghost.
      setActiveMilestoneRowId(typeof milestoneId === 'number' ? milestoneId : null);
      return;
    }
    setActiveMilestoneRowId(null);
    setActiveTaskId(Number(event.active.id));
  }

  // Quick 260914-ims — an Escape-aborted or pointer-lost drag has no
  // onDragEnd; without this the ghost would survive it.
  function handleDragCancel(_event: DragCancelEvent) {
    setActiveTaskId(null);
    setActiveMilestoneRowId(null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveTaskId(null);
    setActiveMilestoneRowId(null);
    const { active, over } = event;
    if (!over) return;

    const dragType = active.data.current?.type;
    const overType = over.data.current?.type;

    // Branch 1 — column reorder (existing behavior).
    if (dragType === 'column' && overType === 'column') {
      const fromStateId = active.data.current?.stateId as number | undefined;
      const toStateId = over.data.current?.stateId as number | undefined;
      if (fromStateId == null || toStateId == null || fromStateId === toStateId) return;
      // Phase 51 — filter out synthetic Triage column (taskStateId=null);
      // the Triage column is always leading and cannot be reordered.
      const orderedIds = (data?.columns ?? [])
        .map((c) => c.taskStateId)
        .filter((id): id is number => id !== null);
      const oldIndex = orderedIds.indexOf(fromStateId);
      const newIndex = orderedIds.indexOf(toStateId);
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(orderedIds, oldIndex, newIndex);
      handleReorderColumns(next);
      return;
    }

    // Branch 1b (quick 260914-huu) — milestone swimlane row reorder. Mirrors
    // Branch 1's shape so a missed drop (mismatched type, null/equal ids, an
    // id not found in the ordered list) stays a harmless no-op — never a
    // partial or crashing write.
    if (dragType === 'milestone-row' && overType === 'milestone-row') {
      const fromMilestoneId = active.data.current?.milestoneId as number | null | undefined;
      const toMilestoneId = over.data.current?.milestoneId as number | null | undefined;
      if (
        fromMilestoneId == null ||
        toMilestoneId == null ||
        fromMilestoneId === toMilestoneId
      ) {
        return;
      }
      const oldIndex = orderedMilestoneIds.indexOf(fromMilestoneId);
      const newIndex = orderedMilestoneIds.indexOf(toMilestoneId);
      if (oldIndex < 0 || newIndex < 0) return;
      onReorderMilestones(arrayMove(orderedMilestoneIds, oldIndex, newIndex));
      return;
    }

    // Branch 2 — task → swimlane cell (Phase 31 D-22 / GOAL-05).
    // Replaces the Phase 30 column-body and sidebar-row branches with a
    // single per-cell handler that fires the state and milestone mutations
    // sequentially (D-03 — never Promise.all, which races the cache
    // invalidations).
    if (overType === 'swimlane-cell') {
      // CR-01 fix — do NOT gate the entire branch on canManageMilestones.
      // Pre-Phase 31 the legacy KanbanColumn drop allowed any internal actor
      // (admin / PM / project assignee — see setTaskState in
      // taskMutationService.ts) to drag tasks between states. Gating the
      // whole branch on canManageMilestones (admin OR PM only) silently
      // dropped that capability for assignees. Per-mutation server-side
      // authorization (assignTaskMilestone is admin/PM-only on the backend)
      // is the source of truth for the milestone-change gate; we only
      // suppress the milestone mutation locally so we don't fire a
      // pre-known-403 PATCH.
      const taskId = Number(active.id);
      // CR-01 / WR-09 — defense-in-depth: ignore drags whose active.id is not
      // a positive integer (e.g. optimistic temp ids `-Date.now()` from
      // useCreateTask). Sending those to the server would 404/500.
      if (
        !Number.isFinite(taskId) ||
        !Number.isInteger(taskId) ||
        taskId <= 0
      ) {
        return;
      }
      const toMilestoneId = (over.data.current?.milestoneId ?? null) as number | null;
      const toStateId = over.data.current?.taskStateId as number | undefined;
      if (toStateId == null) return;

      // Resolve the source row directly from the polled board data (rather
      // than relying on activeTask state, which depends on onDragStart having
      // fired). This makes the handler resilient to drag fixtures that wire
      // only onDragEnd (Wave 0 swimlane.test.tsx), and also picks up the
      // freshest milestone/state if a sibling mutation has already updated
      // the cache mid-drag.
      let sourceTask: TaskCardData | undefined = activeTask;
      if (!sourceTask && data) {
        for (const col of data.columns) {
          const found = col.tasks.find((t) => t.id === taskId);
          if (found) {
            sourceTask = found;
            break;
          }
        }
      }
      if (!sourceTask) return;

      // MKS-419 — merged parent + subprojects board. A merged column's
      // `taskStateId` is only its REPRESENTATIVE state (the parent's when the
      // parent has the column), and `projectIds` lists every project that has a
      // state of that name.
      if (includeSubprojects && data) {
        // The swimlane row the card was dropped into: a subproject lane carries its
        // project, every parent row (milestone or "No milestone") has none.
        const rowProjectId = (over.data.current?.rowProjectId ?? projectIdNum) as number;
        // A drop into a row of ANOTHER project would change the card's project, which
        // a drag never does: refused, no request. Cards still move between columns
        // inside their own lane, and a parent card between the parent's milestone rows.
        if (sourceTask.projectId !== rowProjectId) {
          setToast({
            variant: 'error',
            message:
              'Couldn\u2019t move task: a task can\u2019t be moved into another project\u2019s row',
          });
          return;
        }
        const targetColumn = data.columns.find(
          (c) => !c.isTriage && c.taskStateId === toStateId,
        );
        // The UI blocks a drop onto a column the card's own project lacks: no
        // request is sent (the server would answer 400 / the cross-project guard).
        if (
          targetColumn?.projectIds !== undefined &&
          !targetColumn.projectIds.includes(sourceTask.projectId)
        ) {
          const owner = subprojectsById?.get(sourceTask.projectId);
          setToast({
            variant: 'error',
            message: `Couldn\u2019t move task: ${owner?.slug ?? projectName} has no "${targetColumn.name}" column`,
          });
          return;
        }
        if (sourceTask.projectId !== projectIdNum) {
          // A subproject card: move it to the column with the target's NAME in
          // its OWN project (the server resolves the name). The cell's milestone
          // belongs to the parent, so NO milestone PATCH is ever sent for it.
          if (!targetColumn) return;
          const sourceColumn = data.columns.find((c) =>
            c.tasks.some((t) => t.id === taskId),
          );
          // Same merged column → silent no-op.
          if (sourceColumn && sourceColumn.taskStateId === targetColumn.taskStateId) return;
          void (async () => {
            try {
              await mutation.mutateAsync({
                type: 'state',
                taskId,
                projectId,
                audience,
                toStateName: targetColumn.name,
                includeSubprojects: true,
              });
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : 'Server error';
              setToast({
                variant: 'error',
                message: `Couldn\u2019t move task: ${msg}`,
              });
            }
          })();
          return;
        }
      }

      // CONTEXT D-04 — same-cell silent no-op (no mutation calls).
      if (
        sourceTask.taskStateId === toStateId &&
        sourceTask.milestoneId === toMilestoneId
      ) {
        return;
      }

      // CONTEXT D-03 — state first, milestone second. CR-04 fix: actually
      // enforce completion order (not just invocation order) by awaiting
      // mutateAsync sequentially. Previously both PATCHes were fire-and-
      // forget so HTTP response order was non-deterministic; if the
      // milestone PATCH landed first its onSettled invalidation could
      // refetch the pre-state-PATCH row and the cache would briefly hold
      // the stale state. Each mutation onSettled still invalidates the
      // board cache, so partial failure reconciles automatically on the
      // next poll.
      const wantsState = sourceTask.taskStateId !== toStateId;
      // CR-01 fix: only fire the milestone mutation when the actor has
      // milestone-management authority (admin OR project PM). For project
      // assignees the milestone PATCH would be rejected by the server with
      // 403; suppressing it locally avoids the pre-known failure (and the
      // confusing toast it would produce). The state mutation is still
      // dispatched, which is the legacy assignee capability we are
      // preserving.
      const wantsMilestone =
        canManageMilestones && sourceTask.milestoneId !== toMilestoneId;

      void (async () => {
        if (wantsState) {
          try {
            await mutation.mutateAsync({
              type: 'state',
              taskId,
              projectId,
              audience,
              toStateId,
              ...mergedMutationOpts,
            });
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Server error';
            setToast({
              variant: 'error',
              message: `Couldn\u2019t move task: ${msg}`,
            });
            // Don't fall through to the milestone PATCH on a state failure
            // \u2014 the user expected an atomic move.
            return;
          }
        }
        if (wantsMilestone) {
          try {
            await assignMilestoneMutation.mutateAsync({
              taskId,
              milestoneId: toMilestoneId,
              projectId,
              audience,
            });
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Server error';
            setToast({
              variant: 'error',
              message: `Couldn\u2019t update milestone: ${msg}`,
            });
          }
        }
      })();
      return;
    }
  }

  return (
    // Petra UAT 2026-05-19 — inline style bypasses Tailwind JIT (which may
    // not generate arbitrary calc() classes in some HMR states) AND the
    // flex-chain height propagation that broke somewhere upstream (body
    // was 20 073 px tall per DevTools probe → some ancestor refused to
    // contain height). 100vh - 4rem = (viewport - typical app chrome);
    // the scroll container inside has `overflow-x-scroll` + `overflow-y-auto`
    // so the horizontal scrollbar pins to (viewport_bottom - 4rem - padding).
    <main
      className="flex flex-col overflow-hidden"
      style={{ height: 'calc(100vh - 4rem)' }}
    >
      {/* Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor):
          page-level chrome bg-white/border-gray-200/text-gray-* swapped to
          ms-* tokens. amber-700 'Task no longer available' status retained
          (semantic warning palette per D-Tokens). */}
      <header className="px-4 py-4 border-b border-ms-border bg-ms-surface shrink-0 flex items-center justify-between gap-3">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            {canOpenProjectDetail ? (
              <Link
                to={`/projects/${projectIdNum}`}
                className="text-xl font-semibold text-ms-text-strong truncate hover:underline"
              >
                {projectName}
              </Link>
            ) : (
              <h1 className="text-xl font-semibold text-ms-text-strong truncate">{projectName}</h1>
            )}
            {/* Phase 129 (D-04) — entry to the Engineering tab for any team member the access probe admits. */}
            <ProjectEngineeringLink projectId={projectIdNum} audience={audience} />
          </div>
          {openTaskNotFound && (
            <p
              className="text-xs text-amber-700"
              role="status"
              data-testid="open-task-notfound"
            >
              Task no longer available.
            </p>
          )}
        </div>
        {/* Quick task 260611-gre — Board/List segmented control. Renders for
            both audiences (no gate); placement mirrors Linear (leftmost view control). */}
        <BoardListSwitcher value={viewMode} onChange={handleViewModeChange} />

        {/* MKS-419 — "Include subprojects". Rendered only behind the release
            flag, on the internal Board view of a project that HAS subprojects.
            Off by default; remembered per project for the session. */}
        {showSubprojectsToggle && (
          <label className="inline-flex items-center gap-1.5 shrink-0 cursor-pointer select-none text-[12px] font-medium text-ms-text-secondary">
            <input
              type="checkbox"
              role="switch"
              data-testid="include-subprojects-toggle"
              checked={subprojectsPreference}
              onChange={(e) => handleSubprojectsToggle(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-ms-border accent-ms-orange"
            />
            Include subprojects
          </label>
        )}

        {/* MKS-142 — full-text search over ticket code / name / description.
            Renders for both audiences (D-05, like BoardListSwitcher). State is
            ephemeral (D-03): no URL param, no sessionStorage. Esc clears; the
            slide-over's Esc handler can't collide — its focus trap keeps this
            input unfocusable while the panel is open (Phase 41 Plan 41-17). */}
        <label
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md border ${
            searchActive
              ? 'border-ms-orange/40 bg-ms-orange/5 text-ms-orange-dark'
              : 'border-ms-border bg-ms-surface text-ms-text-secondary'
          }`}
        >
          <MagnifyingGlassIcon aria-hidden="true" className="h-3.5 w-3.5 opacity-70" />
          <input
            type="search"
            maxLength={120}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearchQuery('');
            }}
            placeholder="Search tickets"
            aria-label="Search tickets by code, name or description"
            data-testid="board-search-input"
            className="w-44 bg-transparent border-none p-0 text-[12px] focus:outline-none placeholder:text-ms-text-secondary"
          />
          {searchActive && (
            <>
              <span data-testid="board-search-count" className="whitespace-nowrap tabular-nums">
                {searchCounts.shown} / {searchCounts.total}
              </span>
              <button type="button" aria-label="Clear search" onClick={() => setSearchQuery('')}>
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </label>

        {/* MKS-134 — Filters popover (assignee / status / milestone / created /
            last update). MKS-134 (external surface) supersedes the original
            D-07 "internal only" gate: every option list below is derived from
            `data`, the board response already scoped server-side to this
            audience (assignees via collectBoardAssignees(data?.columns ?? []),
            statuses via statusOptions, milestones via useMilestones's
            audience-aware transport) — no internal-only data is introduced by
            rendering this for external users. Gated on board data loaded — NOT
            on boardAssignees.length, so status/milestone/date filters stay
            reachable on a board where every task is unassigned. */}
        {data != null && data.columns.length > 0 && (
          <BoardFilterBar
            value={effectiveFilters}
            onChange={setFilters}
            assignees={boardAssignees}
            statuses={statusOptions}
            milestones={milestoneOptions}
            labels={boardLabels}
            projects={projectFilterOptions}
            /* MKS-510 — undefined while the flag is off: no Creator row. */
            creators={boardCreatorFilter ? boardCreators : undefined}
            shown={filterCounts.shown}
            total={filterCounts.total}
          />
        )}
        {/* Quick 260916-fk0 — column ordering. Same mount gate as
            BoardFilterBar: it only makes sense once a board is loaded. */}
        {data != null && data.columns.length > 0 && (
          <BoardSortControl value={sortMode} onChange={setSortMode} />
        )}
        {/* MKS-500 — the slim/full column-header toggle. Same data gate as the
            two controls above, plus the Board view: it changes the swimlane's
            header band, which the List view does not render. */}
        {viewMode === 'board' && data != null && data.columns.length > 0 && (
          <SlimHeaderToggle value={slimHeader} onToggle={toggleSlimHeader} />
        )}
        {/* MKS-163 follow-up — one-click priority filtering without opening
            the Filters panel. Mounted with the SAME gate as BoardFilterBar
            (data loaded + has columns) so the two controls appear together
            for both audiences; `filters.priority` is the same
            BoardFilterState slot BoardFilterBar's own "Clear filters" still
            resets. */}
        {data != null && data.columns.length > 0 && (
          <PriorityFilterToggle
            value={filters.priority}
            onChange={(priority) => setFilters({ ...filters, priority })}
          />
        )}
        {audience === 'external' && (
          <ExternalCreateTaskButton onClick={() => setCreateOpen(true)} />
        )}
        {canManageMilestones && (
          <button
            type="button"
            onClick={() => setMilestoneModal({ kind: 'create' })}
            aria-label="Create new milestone"
            className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-ms-border bg-ms-surface px-3 py-1.5 text-sm font-medium text-ms-text hover:bg-ms-canvas"
          >
            <PlusIcon className="w-4 h-4" />
            New milestone
          </button>
        )}
        {canInviteExternal && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-ms-border bg-ms-surface px-3 py-1.5 text-sm font-medium text-ms-text hover:bg-ms-canvas"
          >
            External users
          </button>
        )}
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div
          ref={boardScrollRef}
          // Petra UAT 2026-05-19 — `min-w-0` + `min-h-0` are mandatory on a
          // `flex-1` scroll container. Without them flex children default to
          // `min-width/min-height: auto` (content size) so the kanban grid
          // (wider than viewport at 5+ columns) blows up the scroll container
          // past its allocated flex slot — the horizontal scrollbar then
          // shows up at the bottom of the FULL content height instead of at
          // the viewport edge. With these mins the scroll container stays
          // bounded to the flex allocation and the browser pins the native
          // horizontal scrollbar to the bottom edge of the visible viewport.
          className="flex-1 min-w-0 min-h-0 overflow-x-auto overflow-y-auto p-4 select-none"
          // Native DOM dragstart (NOT the dnd-kit DndContext onDragStart above):
          // suppresses macOS Chrome's parallel HTML5 drag of selected text,
          // which renders as a blue-bordered ghost next to the DragOverlay.
          onDragStart={(e) => e.preventDefault()}
        >
          {isLoading && <BoardSkeleton />}

          {isError && (
            <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
              {/* Phase 41 Plan 41-17 — text-gray-500 → text-ms-text-secondary. */}
              <p className="text-sm font-medium text-ms-text-secondary">
                Couldn&apos;t load board.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-primary-600 hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && data && data.columns.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              {/* Phase 41 Plan 41-17 — text-gray-500 → text-ms-text-secondary. */}
              <p className="text-sm font-medium text-ms-text-secondary">
                This project has no states defined yet. Contact an admin.
              </p>
            </div>
          )}

          {/* Quick task 260610-kv6 — STATE_IN_USE conflict banner. Mirrors
              ProjectFormPage:2065-2090 (single-banner contract per D-20). The
              deep-link routes the user to the affected tasks so they can move
              them off the state before retrying delete. */}
          {deleteConflict && (
            <div
              className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center gap-3"
              role="alert"
            >
              <span>
                Cannot delete &quot;{deleteConflict.stateName}&quot; — used by{' '}
                {deleteConflict.taskCount} task{deleteConflict.taskCount === 1 ? '' : 's'}.
                Move tasks to another state first.
              </span>
              <Link
                to={`/manager/tasks?projectId=${projectId}&stateId=${deleteConflict.stateId}`}
                className="underline hover:text-red-900"
              >
                View tasks →
              </Link>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setDeleteConflict(null)}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Quick task 260611-gre — branch on viewMode for the success state.
              Loading / error / empty-state blocks above are shared between both views.
              List view: ProjectListView (grouped by state, Linear-style).
              Board view: existing DndContext + TriageColumn + KanbanSwimlane. */}
          {!isLoading && !isError && (triageColumn || (sortedData && sortedData.columns.length > 0)) && (
            viewMode === 'list' ? (
              <ProjectListView
                projectId={projectId}
                projectIdNum={projectIdNum}
                audience={audience}
                columns={sortedData?.columns ?? []}
                triageColumn={sortedTriageColumn}
                onRowClick={handleCardClick}
                onStateChangeError={(message) =>
                  setToast({ variant: 'error', message: `Couldn’t update status: ${message}` })
                }
                // 260913-jpy — per-project staleness indicator config.
                stalenessConfig={stalenessConfig}
              />
            ) : (
              <div
                // min-h-full (NOT h-full): the scroll container is a block, so
                // h-full would clamp this row to the container's VISIBLE height
                // — the sticky column headers' containing block would end one
                // viewport down and the band would detach mid-scroll (MKS-173).
                // min-h-full keeps the full-height look while letting the row
                // grow with content so sticky travel spans the whole board.
                //
                // MKS-184 — on a board with NO milestones that clamp is exactly
                // what we want: a single row of columns that each scroll on
                // their own, with no page scroll left for the header band to
                // detach from. `h-full` here is what gives KanbanSwimlane (and
                // TriageColumn, a stretched sibling) a definite height to
                // divide up. Boards WITH milestones keep min-h-full and the
                // page scroll — variant (a).
                className={`flex gap-4 ${boardFillsViewportHere ? 'h-full min-h-0' : 'min-h-full'}`}
              >
                {/* Triage column — only for internal audience with a triage column */}
                {triageColumn && audience === 'internal' && (
                  <TriageColumn
                    projectId={projectId}
                    projectIdNum={projectIdNum}
                    tasks={sortedTriageColumn?.tasks ?? []}
                    canManage={canManageMilestones || isAdmin}
                    projectTaskTypeId={projectQuery.data?.taskTypeId ?? null}
                    tiers={slaTiers}
                    subprojectsById={subprojectsById}
                  />
                )}
                {sortedData && sortedData.columns.length > 0 && (
                  // MKS-184 — min-h-0 so this flex item can be shorter than its
                  // content in fill mode; without it the swimlane's own h-full
                  // would resolve against a content-sized box and nothing would
                  // scroll inside the columns.
                  <div className="flex-1 min-w-0 min-h-0">
                    <KanbanSwimlane
                      projectId={projectId}
                      boardData={sortedData}
                      audience={audience}
                      canManageMilestones={canManageMilestones}
                      canReorderColumns={canReorderColumnsHere}
                      canManageStates={canManageStatesHere}
                      subprojectsById={subprojectsById}
                      subprojectLanes={subprojectLanes}
                      hideParentRows={hideParentRows}
                      canReorderRows={canReorderMilestoneRows}
                      // MKS-500 — a defined value is what makes the swimlane
                      // controlled and drops its own corner-cell toggle.
                      slimHeader={slimHeader}
                      onRenameState={handleRenameState}
                      onRecolorState={handleRecolorState}
                      onDeleteState={handleDeleteState}
                      onToggleTerminalState={handleToggleTerminalState}
                      onToggleInProgressState={handleToggleInProgressState}
                      trailingHeaderSlot={
                        canManageStatesHere ? (
                          <AddStateColumn
                            stateCount={
                              sortedData.columns.filter(
                                (c) => c.taskStateId !== null,
                              ).length
                            }
                            onAdd={handleAddState}
                          />
                        ) : undefined
                      }
                      isDraggingFromState={activeTaskSourceStateId}
                      // MKS-487 — "a task card is in flight". Read from
                      // activeTaskId (not activeTaskSourceStateId, which is
                      // null for a card whose state id is null) so the swimlane
                      // reveals every empty-hidden column for the whole drag.
                      isDraggingCard={activeTaskId !== null}
                      // MKS-184 — same flag that picked h-full for the row
                      // wrapper above; the two MUST move together.
                      fillViewportHeight={boardFillsViewportHere}
                      onCardClick={handleCardClick}
                      onFlip={handleFlip}
                      onCreateMilestone={() => setMilestoneModal({ kind: 'create' })}
                      onEditMilestone={(milestone) =>
                        setMilestoneModal({ kind: 'edit', milestone })
                      }
                      onDeleteMilestone={(milestone) =>
                        setMilestoneModal({ kind: 'delete-confirm', milestone })
                      }
                      // Phase 41 Plan 41-08 (Surface 5d-A) — thread the parent
                      // project's taskTypeId so SLA-typed projects get inline R/X
                      // timer bars on every card. Per checker I-09 the value
                      // flows top-down from the project query that's already
                      // loaded for the column-reorder gate; never accessed via
                      // task.project.taskTypeId (does not exist on TaskCardData).
                      projectTaskTypeId={projectQuery.data?.taskTypeId ?? null}
                      // 260913-jpy — per-project staleness indicator config.
                      stalenessConfig={stalenessConfig}
                    />
                  </div>
                )}
              </div>
            )
          )}
        </div>
        <DragOverlay>
          {activeTask ? (
            <TaskCardDragOverlay
              task={activeTask}
              projectTaskTypeId={projectQuery.data?.taskTypeId ?? null}
            />
          ) : activeMilestoneRow ? (
            <MilestoneRowDragOverlay milestone={activeMilestoneRow} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Phase 41 Plan 41-17 (Task 2) — TaskDetailModal replaced by
       * TaskDetailSlideOver (right-anchored panel) per CONTEXT amendment
       * 2026-05-12 D-TaskSlideOver. All props mirror the former modal's
       * shape (pure layout swap; hooks rewire 1:1). */}
      <TaskDetailSlideOver
        task={slideOverTask}
        audience={audience}
        isOpen={selectedTask !== null}
        onClose={handleCloseModal}
        // MKS-419 — a subproject card is fed that subproject's own context.
        canManageMilestones={
          selectedSubprojectId !== undefined ? canManageOwnProject : canManageMilestones
        }
        // MKS-364 — who may lock the ticket for time booking. The same
        // authority as column reorder / milestone management / external invite
        // above: admin OR the project's PM.
        canManageTimeBookingLock={
          selectedSubprojectId !== undefined ? canManageOwnProject : canManageMilestones
        }
        // Phase 41 Plan 41-08 (Surface 5d-A) — same prop the kanban surface
        // forwards; the slide-over mounts the same SlaTaskCard inline so
        // the bars are uniform across kanban cards AND task detail per
        // CONTEXT D-4 ("inline only — uniform density across contexts").
        projectTaskTypeId={
          selectedSubprojectId !== undefined
            ? (ownProjectQuery.data?.taskTypeId ?? null)
            : (projectQuery.data?.taskTypeId ?? null)
        }
        // 260913-jpy — per-project staleness indicator config.
        stalenessConfig={selectedSubprojectId !== undefined ? ownStalenessConfig : stalenessConfig}
        // UAT 2026-05-22 #2 — board columns drive the status pill colour
        // and the change-status popover inside the slide-over header.
        columns={selectedSubprojectId !== undefined ? ownBoardQuery.data?.columns : data?.columns}
        // MKS-384 — `?comment={id}`: open the thread on that comment.
        deepLinkCommentId={deepLinkCommentId}
        // MKS-144 — "Open full screen" navigates to the permanent full-page
        // ticket view. Internal-only (external portal never sees the
        // internal /tasks/* route); undefined while no task is selected.
        onOpenFullPage={
          audience === 'internal' && selectedTask
            ? () => navigate(`/tasks/${selectedTask.id}`)
            : undefined
        }
        onUpdateError={(message) =>
          setToast({ variant: 'error', message: `Couldn’t update task: ${message}` })
        }
        onDeleteError={(message) =>
          setToast({ variant: 'error', message: `Couldn’t delete task: ${message}` })
        }
      />

      {/* Phase 30 — milestone create / edit dialog. */}
      {(milestoneModal.kind === 'create' || milestoneModal.kind === 'edit') && (
        <MilestoneFormModal
          isOpen
          onClose={() => setMilestoneModal({ kind: 'closed' })}
          projectId={projectId}
          mode={milestoneModal.kind}
          milestone={
            milestoneModal.kind === 'edit' ? milestoneModal.milestone : undefined
          }
          onSuccess={(verb) =>
            setToast({
              variant: 'success',
              message:
                verb === 'created' ? 'Milestone created.' : 'Milestone saved.',
            })
          }
          onError={(_verb, msg) =>
            setToast({
              variant: 'error',
              message: `Couldn\u2019t save milestone: ${msg}`,
            })
          }
        />
      )}

      {/* Phase 30 — milestone delete confirmation (UI-SPEC § 4). */}
      {milestoneModal.kind === 'delete-confirm' && (
        <ConfirmModal
          isOpen
          onClose={() => setMilestoneModal({ kind: 'closed' })}
          onConfirm={() => {
            const target = milestoneModal.milestone;
            deleteMilestoneMutation.mutate(target.id, {
              onSuccess: () => {
                setMilestoneModal({ kind: 'closed' });
                setToast({
                  variant: 'success',
                  message: 'Milestone deleted.',
                });
              },
              onError: (err: unknown) => {
                const msg = err instanceof Error ? err.message : 'Server error';
                setToast({
                  variant: 'error',
                  message: `Couldn\u2019t delete milestone: ${msg}`,
                });
              },
            });
          }}
          title="Delete milestone?"
          message={`Delete "${milestoneModal.milestone.name}"? Tasks assigned to it will move to "No milestone". This cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          isLoading={deleteMilestoneMutation.isPending}
          error={(deleteMilestoneMutation.error as Error | null)?.message ?? null}
        />
      )}

      {audience === 'external' && (
        <ExternalCreateTaskModal
          projectId={projectId}
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            // Phase 25 D-23 — scroll the first column into view after a
            // successful create so the optimistic prepend is visible.
            window.requestAnimationFrame(() => {
              const firstCol = boardScrollRef.current?.querySelector(
                '[data-column-index="0"]',
              );
              if (firstCol && typeof firstCol.scrollIntoView === 'function') {
                firstCol.scrollIntoView({ behavior: 'smooth', inline: 'start' });
              }
            });
          }}
        />
      )}

      {canInviteExternal && (
        <InviteExternalModal
          projectId={projectIdNum}
          isOpen={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSuccess={(message) => setToast({ variant: 'success', message })}
          onError={(message) =>
            setToast({ variant: 'error', message: `Couldn’t invite: ${message}` })
          }
        />
      )}

      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </main>
  );
}

function BoardSkeleton() {
  // Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor):
  // border-gray-200 → border-ms-border, bg-gray-50 → bg-ms-canvas,
  // bg-gray-100 → bg-ms-surface-2 (slightly stronger so the pulse is visible
  // against the ms-canvas column background).
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {[1, 2, 3].map((col) => (
        <div
          key={col}
          className="flex w-72 shrink-0 flex-col rounded-lg border border-ms-border bg-ms-canvas"
        >
          <div className="h-10 mx-2 my-2 rounded-lg bg-ms-surface-2 animate-pulse" />
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((card) => (
              <div key={card} className="h-16 rounded-lg bg-ms-surface-2 animate-pulse" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
